'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { Grid3X3, Users, DollarSign, TrendingUp, Loader2 } from 'lucide-react';
import { BoardroomIcon } from '@/components/shared';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CoinImage } from '@/components/shared/coin-image';
import { formatUSD, cn, isModifiedClick, parseGridId, gridHref } from '@/lib/utils';
import { useEthPrice, ethToUsd, useTransactionQueue, useTokenPrices, tokenToUsd, effectiveTokenAddress } from '@/hooks';
import { WETH_ADDRESS, HV_TAKEOVER_GRID_ID } from '@/lib/constants/addresses';
import { encodeFunctionData, type Hex } from 'viem';
import { useQueryClient } from '@tanstack/react-query';
import type { CreatorGridSummary } from '@/lib/data-sources';

export interface CreatorGridData extends CreatorGridSummary {
  claimableTradingRewards: string;
  /** On-chain `taxRevenue(gridId)` reading. `null` (or `undefined`)
   *  signals the multicall failed and the consumer should fall back
   *  to `pendingTaxRevenue`; a string (including `'0'`) is trusted as
   *  contract truth. See `app/api/profile/[address]/grids/route.ts`
   *  for the producer side. */
  onchainTaxRevenue?: string | null;
}

interface CreatorGridCardProps {
  grid: CreatorGridData;
  isOwnProfile: boolean;
  onNavigate?: () => void;
  /** Read-only profile list: identity + seat occupancy only (no claim / revenue columns). */
  readOnly?: boolean;
}

export function CreatorGridCard({ grid, isOwnProfile, onNavigate, readOnly }: CreatorGridCardProps) {
  const { ethPrice } = useEthPrice();
  const { executeActions } = useTransactionQueue();
  const queryClient = useQueryClient();
  const [isClaiming, setIsClaiming] = useState(false);

  const yieldAddr = effectiveTokenAddress(grid.yieldToken);
  const { prices: tokenPrices } = useTokenPrices(yieldAddr && yieldAddr !== WETH_ADDRESS ? [yieldAddr] : []);
  const yieldPriceUsd = yieldAddr === WETH_ADDRESS
    ? (ethPrice || tokenPrices[WETH_ADDRESS] || 0)
    : (tokenPrices[yieldAddr] || 0);

  const isBoardroom = grid.id === HV_TAKEOVER_GRID_ID;
  const primaryAsset = grid.assets[0];
  const gridName = isBoardroom ? 'Boardroom' : (primaryAsset?.symbol || grid.name || `Grid #${parseGridId(grid.id).numericId}`);
  const gridImage = primaryAsset?.iconUrl || grid.imageUrl;

  // `??` (not `||`): `'0'` is a successful onchain read that we
  // must trust — falling back to the indexer's `pendingTaxRevenue`
  // when the contract bucket is genuinely empty surfaces a phantom
  // amount and `withdrawTaxRevenue(gridId)` would preflight-revert
  // with `NothingToWithdraw()` (`0xd0d04f60`). Only a `null` /
  // `undefined` `onchainTaxRevenue` (= multicall failed; see the
  // route's producer side) lets us reach for the indexer.
  const claimableTaxUsdc = BigInt(
    grid.onchainTaxRevenue ?? grid.pendingTaxRevenue ?? '0',
  );
  const claimableTradingWei = BigInt(grid.claimableTradingRewards);
  const claimableTradingUsd = tokenToUsd(claimableTradingWei, grid.yieldTokenDecimals, yieldPriceUsd);
  const hasTradingRewards = claimableTradingWei > 0n;
  const hasTaxRevenue = claimableTaxUsdc > 0n;
  const hasClaimable = hasTradingRewards || hasTaxRevenue;

  // Seats currently held and paying Harberger tax. Mirrors the lobby
  // board-card calculation in `features/lobby/data/grid-to-board.ts`
  // (`taxPayingSeatCount`): use `activeSeatCount` directly and clamp
  // against `totalSeats` to absorb the transient indexer overshoot
  // that can happen during reorgs.
  //
  // The previous implementation used `seatsSoldFromCreator`, which
  // under-counted on boards where seats were claimed without ever
  // sitting on the creator (e.g. the Boardroom — every seat goes
  // straight to a player), producing the visible "77 / 256 minted"
  // mismatch versus the same board's lobby card showing all 256 as
  // taxable.
  const occupied = Math.max(0, Math.min(grid.activeSeatCount, grid.totalSeats));

  const invalidateAfterClaim = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['creator-grids'] });
    queryClient.invalidateQueries({ queryKey: ['claimable-fees'] });
    queryClient.invalidateQueries({ queryKey: ['wallet'] });
  }, [queryClient]);

  const handleClaim = useCallback(async () => {
    if (!hasClaimable || isClaiming) return;
    setIsClaiming(true);

    try {
      const { UGM_ABI, getContractAddresses } = await import('@/lib/contracts');
      const addresses = getContractAddresses();

      type Step = { id: string; label: string; description: string; call: { to: Hex; data: Hex } };
      const steps: Step[] = [];

      const target = (grid.managerAddress || addresses.UGM) as Hex;

      if (hasTradingRewards) {
        const claimData = encodeFunctionData({
          abi: UGM_ABI,
          functionName: 'claimFees',
          args: [[BigInt(parseGridId(grid.id).numericId)]],
        });
        steps.push({
          id: `claim-grid-${grid.id}`,
          label: 'Claim Trading Rewards',
          description: `Collecting rewards from ${gridName}`,
          call: { to: target, data: claimData },
        });
      }

      if (hasTaxRevenue) {
        const taxData = encodeFunctionData({
          abi: UGM_ABI,
          functionName: 'withdrawTaxRevenue',
          args: [BigInt(parseGridId(grid.id).numericId)],
        });
        steps.push({
          id: `withdraw-tax-${grid.id}`,
          label: 'Withdraw Tax Revenue',
          description: `${formatUSD(claimableTaxUsdc)} USDC from ${gridName}`,
          call: { to: target, data: taxData },
        });
      }

      const totalUsd = claimableTradingUsd + Number(claimableTaxUsdc) / 1e6;
      await executeActions(
        `Claim ${formatUSD(totalUsd)}`,
        steps,
        { onSuccess: invalidateAfterClaim },
      );
    } finally {
      setIsClaiming(false);
    }
  }, [hasClaimable, hasTradingRewards, hasTaxRevenue, isClaiming, grid.id, gridName, claimableTradingUsd, claimableTaxUsdc, executeActions, invalidateAfterClaim]);

  if (readOnly) {
    return (
      <div className="rounded-[4px] border border-amber-500/15 bg-black/35 overflow-hidden transition-colors hover:border-amber-500/25">
        <Link
          href={gridHref(grid.id)}
          className="flex items-center gap-3 p-3 min-w-0 hover:bg-white/[0.03] transition-colors"
          onClick={
            onNavigate
              ? (e) => {
                  if (!isModifiedClick(e)) {
                    e.preventDefault();
                    onNavigate();
                  }
                }
              : undefined
          }
        >
          {isBoardroom ? (
            <div className="w-10 h-10 rounded-[4px] border border-amber-500/20 bg-black/40 shrink-0 flex items-center justify-center">
              <BoardroomIcon size="lg" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-[4px] overflow-hidden border border-amber-500/20 bg-black/40 shrink-0">
              <CoinImage src={gridImage} alt={gridName} symbol={gridName} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm text-zinc-100 truncate">{gridName}</p>
            <p className="text-[10px] font-mono text-zinc-500 mt-0.5">
              {occupied}/{grid.totalSeats} seats
            </p>
          </div>
          <Grid3X3 className="h-4 w-4 text-zinc-600 shrink-0" aria-hidden />
        </Link>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden transition-colors hover:border-border/80">
      <CardContent className="p-0">
        <div className="flex items-stretch">
          {/* Grid identity */}
          <Link
            href={gridHref(grid.id)}
            className="flex items-center gap-3 p-4 flex-1 min-w-0 hover:bg-muted/30 transition-colors"
            onClick={onNavigate ? (e) => { if (!isModifiedClick(e)) { e.preventDefault(); onNavigate(); } } : undefined}
          >
            {isBoardroom ? (
              <div className="w-10 h-10 rounded-lg border border-border/50 bg-muted/30 shrink-0 flex items-center justify-center">
                <BoardroomIcon size="lg" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg overflow-hidden border border-border/50 bg-muted/30 shrink-0">
                <CoinImage src={gridImage} alt={gridName} symbol={gridName} />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{gridName}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {occupied}/{grid.totalSeats} seats
                </span>
              </div>
            </div>
          </Link>

          {/* Rewards section */}
          <div className="flex items-center gap-3 px-4 border-l border-border/40">
            {/* Tax revenue */}
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground leading-tight">Tax Revenue</p>
              <p className={cn(
                "text-sm font-bold font-mono",
                hasTaxRevenue ? "text-amber-500" : "text-muted-foreground"
              )}>
                {hasTaxRevenue ? `+${formatUSD(claimableTaxUsdc)}` : formatUSD(0)}
              </p>
            </div>

            {/* Trading rewards */}
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground leading-tight">Trading Rewards</p>
              <p className={cn(
                "text-sm font-bold font-mono",
                hasTradingRewards ? "text-win-green" : "text-muted-foreground"
              )}>
                {hasTradingRewards ? `+${formatUSD(claimableTradingUsd)}` : formatUSD(0)}
              </p>
            </div>

            {/* Claim button — only shown when there are fees to claim */}
            {isOwnProfile && hasClaimable && (
              <Button
                size="sm"
                variant="ghost"
                disabled={isClaiming}
                onClick={handleClaim}
                className="h-8 text-xs shrink-0 bg-gradient-to-r from-accent-blue via-blue-500 to-indigo-500 hover:from-accent-blue-dark hover:via-blue-600 hover:to-indigo-600 text-white hover:text-white shadow-lg shadow-accent-blue/25"
              >
                {isClaiming ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  'Claim'
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
